import { ArchiveRestoreMenuItem } from "components/Shared/ArchiveRestoreIconButton";
import { useBrandMutation } from "utils/apollo/hooks/brand";
import {
  DocumentType,
  FragmentType,
  getFragmentData,
  gql,
} from "__generated__/gql";
import useAsyncCompleteToast from "components/Shared/useAsyncCompleteToast";
import { assertPresence } from "utils/assertions";
import { updateCachedProductLineSkus } from "components/Products/queries";
import {
  getArchivedProductLineFilter,
  getArchivedSkuFilter,
} from "components/Products/utils";
import {
  clearUnmodifiedFields,
  modifyFieldWithSpec,
  removeRefs,
} from "utils/apollo/cacheHelpers";
import { ArchivedStatus } from "components/Shared/ArchivedStatusButtonGroup";

const fragment = gql(/* GraphQL */ `
  fragment ArchiveSkuMenuItemSku on Sku {
    id
    isArchived
    productLine {
      id
      isArchived
    }
  }
`);

const mutation = gql(/* GraphQL */ `
  mutation UpdateSkuArchivedStatus($skuId: UUID!, $isArchived: Boolean!) {
    updateSku(input: { id: $skuId, patch: { isArchived: $isArchived } }) {
      sku {
        id
        name
        isArchived
        productLine {
          id
          isArchived
          brandId
          archivedSkus: skus(filter: { isArchived: { equalTo: true } }) {
            totalCount
          }
        }
      }
    }
  }
`);

export type OnArchiveSuccess = (data: DocumentType<typeof mutation>) => void;

type Props = {
  onArchiveSuccess?: OnArchiveSuccess;
  sku: FragmentType<typeof fragment> | null | undefined;
};

export default function ArchiveSkuMenuItem({
  onArchiveSuccess,
  sku: skuFragment,
}: Props) {
  const sku = getFragmentData(fragment, skuFragment);

  const isArchivedAction = !sku?.isArchived;

  const [performUpdate, { loading }] = useBrandMutation(mutation, {
    variables: sku
      ? { isArchived: isArchivedAction, skuId: sku.id }
      : undefined,
    update: (cache, result) => {
      const updatedSku = result.data?.updateSku?.sku;
      assertPresence(updatedSku?.productLine);

      const archivedStatus = updatedSku.isArchived
        ? ArchivedStatus.Active
        : ArchivedStatus.Archived;

      if (sku?.isArchived !== updatedSku.isArchived) {
        updateCachedProductLineSkus(
          cache,
          updatedSku.productLine,
          [archivedStatus],
          (existing) => {
            if (!existing) return existing;
            return existing.filter((s) => s.id !== updatedSku.id);
          },
        );
        modifyFieldWithSpec({
          cache,
          typename: "ProductLine",
          id: updatedSku.productLine.id,
          fieldSpec: {
            fieldName: "skusList",
            args: {
              orderBy: "CREATED_AT_ASC",
              filter: getArchivedSkuFilter(
                updatedSku.isArchived
                  ? ArchivedStatus.Archived
                  : ArchivedStatus.Active,
              ),
            },
          },
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          modifier: (existing, details) => details.DELETE,
        });
      }

      if (
        (sku?.productLine?.isArchived !== updatedSku.productLine.isArchived &&
          updatedSku.productLine.isArchived) ||
        (!updatedSku.productLine.isArchived &&
          updatedSku.productLine.archivedSkus.totalCount === 0)
      ) {
        const removedFromProductLine = modifyFieldWithSpec({
          cache,
          fieldSpec: {
            fieldName: "productLinesList",
            args: {
              condition: { brandId: updatedSku.productLine.brandId },
              filter: getArchivedProductLineFilter(archivedStatus),
              orderBy: "CREATED_AT_ASC",
            },
          },
          modifier: removeRefs([updatedSku.productLine]),
        });
        clearUnmodifiedFields(cache, [removedFromProductLine]);
      }
    },
  });

  const { showSuccessToast, showErrorToast } = useAsyncCompleteToast();

  return (
    <ArchiveRestoreMenuItem
      isArchive={isArchivedAction}
      labelSuffix="SKU"
      onClick={() => {
        performUpdate()
          .then((result) => {
            const data = result?.data;
            assertPresence(data);
            const updatedSku = data?.updateSku?.sku;
            assertPresence(updatedSku);
            showSuccessToast(
              `${updatedSku.isArchived ? "Archived" : "Unarchived"} ${
                updatedSku.name
              }`,
            );
            onArchiveSuccess?.(data);
          })
          .catch((reason) => {
            showErrorToast(reason);
          });
      }}
      isDisabled={!sku?.id}
      isLoading={loading}
    />
  );
}
